import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  usePagedCreativeElementsQuery,
  usePinnedCreativeElementsQuery,
  type ElementsFilterParams,
} from "@/lib/queries/creative-element-queries";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type {
  CreativeElementCategory,
  CreativeElementDto,
} from "@repo/api/creative-element";

function getElementsFilterParams(
  activeTab: CreativeElementCategory,
  selectedTags: string[],
): ElementsFilterParams {
  return {
    category: activeTab,
    tags: selectedTags.length > 0 ? [...selectedTags].sort() : undefined,
  };
}

/**
 * Fetches and merges pinned + paged creative elements.
 *
 * Pinned/in-video IDs are snapshotted at mount so that selecting or
 * deselecting an element never causes it to jump in the list.
 */
export function useElementsData({
  activeTab,
  selectedElementIds,
  selectedTags,
}: {
  activeTab: CreativeElementCategory;
  selectedElementIds: string[];
  selectedTags: string[];
}) {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const sourceImages = useEditorStore((state) => state.sourceImages);

  const [pinnedElementIds] = useState(() => {
    const ids = new Set<string>();
    for (const id of selectedElementIds) ids.add(id);
    for (const si of Object.values(sourceImages)) {
      for (const id of si.elementExternalIds) ids.add(id);
    }
    return Array.from(ids);
  });

  const [initialSelectedElementIds] = useState(
    () => new Set(selectedElementIds),
  );

  const [inVideoElementIds] = useState(() => {
    const ids = new Set<string>();
    for (const si of Object.values(sourceImages)) {
      for (const id of si.elementExternalIds) ids.add(id);
    }
    return ids;
  });

  const filterParams = getElementsFilterParams(activeTab, selectedTags);

  const pinnedQuery = usePinnedCreativeElementsQuery(
    teamExternalId,
    pinnedElementIds,
    filterParams,
  );

  const pagedQuery = usePagedCreativeElementsQuery(
    teamExternalId,
    filterParams,
    50,
  );

  const isPending =
    pagedQuery.isPending ||
    (pinnedElementIds.length > 0 && pinnedQuery.isPending);
  const isError = pagedQuery.isError || pinnedQuery.isError;

  const elements = useMemo(() => {
    const pinnedElements = pinnedQuery.data?.elements ?? [];
    const sortedPinnedElements = [...pinnedElements].sort((a, b) => {
      const aSelected = initialSelectedElementIds.has(a.externalId) ? 0 : 1;
      const bSelected = initialSelectedElementIds.has(b.externalId) ? 0 : 1;
      return aSelected - bSelected;
    });
    const pagedElements =
      pagedQuery.data?.pages.flatMap((page) => page.elements) ?? [];
    const pinnedIdSet = new Set(pinnedElements.map((e) => e.externalId));
    return [
      ...sortedPinnedElements,
      ...pagedElements.filter((e) => !pinnedIdSet.has(e.externalId)),
    ];
  }, [pinnedQuery.data, pagedQuery.data, initialSelectedElementIds]);

  const isInVideo = useCallback(
    (externalId: string) => inVideoElementIds.has(externalId),
    [inVideoElementIds],
  );

  return {
    elements,
    isPending,
    isError,
    isInVideo,
    hasNextPage: pagedQuery.hasNextPage,
    isFetchingNextPage: pagedQuery.isFetchingNextPage,
    fetchNextPage: pagedQuery.fetchNextPage,
  };
}

/**
 * Manages scroll-container sizing, row virtualisation, and infinite-scroll
 * triggering for the elements grid.
 *
 * The scroll container ref MUST be attached to a DOM element that is always
 * rendered (no early returns before it), otherwise the ResizeObserver will throw.
 */
const GRID_GAP = 12;

export function useElementsVirtualizer({
  activeTab,
  elements,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  activeTab: CreativeElementCategory;
  elements: CreativeElementDto[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      throw new Error(
        "scrollContainerRef.current is not defined, did you add an early return to this component?",
      );
    }
    const observer = new ResizeObserver((entries) => {
      const size = entries[0]?.contentBoxSize?.[0];
      if (size) setContainerWidth(size.inlineSize);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columnsPerRow = activeTab === "HUMAN" ? 4 : 3;
  const aspectRatio = activeTab === "HUMAN" ? 9 / 16 : 3 / 2;
  const totalRows = Math.ceil(elements.length / columnsPerRow);

  const estimatedRowHeight = useMemo(() => {
    if (containerWidth === 0) return 0;
    const itemWidth =
      (containerWidth - (columnsPerRow - 1) * GRID_GAP) / columnsPerRow;
    return itemWidth / aspectRatio + GRID_GAP;
  }, [containerWidth, columnsPerRow, aspectRatio]);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? totalRows + 1 : totalRows,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 5,
    enabled: estimatedRowHeight > 0,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, estimatedRowHeight]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (estimatedRowHeight === 0) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (lastItem.index >= totalRows - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    totalRows,
    estimatedRowHeight,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return {
    scrollContainerRef,
    columnsPerRow,
    totalRows,
    rowVirtualizer,
    virtualItems,
  };
}
